'use client';

import { useEffect, useState } from 'react';

import { contrastingTextColorFromRgb } from '../utils/color';

export interface ImageAverageColor {
  /** `rgb(r, g, b)` of the image's average pixel — null before load or on CORS failure. */
  color: string | null;
  /** Contrasting text for `color` — `#000000` on light images, `#ffffff` on dark; null before load. */
  contrastingText: string | null;
}

const EMPTY: ImageAverageColor = { color: null, contrastingText: null };

/**
 * Sample an image's average pixel color on a 1x1 canvas (client-only). Used to
 * tint bio surfaces from their banner — the My Life series/course card and the
 * course-detail hero backdrop. Returns nulls until the image decodes; a
 * cross-origin-tainted canvas read throws and the nulls persist (callers keep
 * their own defaults). The URL must be CORS-enabled — raw Firebase Storage
 * URLs are, so pass those rather than a resize-service URL.
 */
export function useImageAverageColor(url?: string | null): ImageAverageColor {
  const [result, setResult] = useState<ImageAverageColor>(EMPTY);

  useEffect(() => {
    if (!url) {
      setResult(EMPTY);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (cancelled) return;
      canvas.width = 1;
      canvas.height = 1;
      ctx.drawImage(image, 0, 0, 1, 1);
      try {
        const [red, green, blue] = ctx.getImageData(0, 0, 1, 1).data;
        setResult({
          color: `rgb(${red}, ${green}, ${blue})`,
          contrastingText: contrastingTextColorFromRgb([red, green, blue]),
        });
      } catch {
        // Cross-origin-tainted canvas — keep nulls; callers use their defaults.
      }
    };
    image.src = url;

    return () => {
      cancelled = true;
    };
  }, [url]);

  return result;
}
